import PinLike from "@/components/PinCard/actions/like";
import PinActionProvider from "@/components/PurePinCard/actions/context";
import { AB_RECOMMEND_CREATE_BOARD } from "@/constants/ABMng";
import { EVENT } from "@/constants/event";
import { USER_BOARD_RECOMMEND_THRESHOLD } from "@/constants/pinCollectThreshold";
import useAppStore from "@/context/store";
import { canEditPinFn } from "@/utils/board";
import useABTest from "@/utils/hooks/useABtest";
import { useBoardMatching } from "@/utils/hooks/useBoardMatching";
import { useRelatedBoards } from "@/utils/hooks/useRelatedBoards";
import { useDebounceFn } from "ahooks";
import { useRouter } from "next/router";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import PurePinButton from "./button";
import PurePinDownload from "./download";
import PurePinEdit from "./edit";
import styles from "./index.module.less";

interface IPinActionsProps {
  pin?: IPin;
  position?: {
    x: number;
    y: number;
  };
  width?: number;
  height?: number;
  onUpdatePin?: (pin: IPin) => void;
  offsetParent?: HTMLElement;
  fullWidth?: boolean;
  showDownload?: boolean;
  hasPinCreated?: boolean;
  contextValue?: {
    holdPinAction?: boolean;
    setHoldPinAction?: (holdPinAction: boolean) => void;
  };
  showLikeBtn?: boolean;
  searchWord?: string;
  currentBoard?: any; // 当前画板信息，用于画板选择器默认值
  enableCurrentBoardPriority?: boolean; // 是否启用当前画板优先逻辑
}

/**
 * 采集卡片操作按钮组件
 * 定位原理：使用绝对定位，根据图片的offset位置，计算出按钮的位置，将组件渲染到卡片组件的offsetParent中
 * @returns
 */
export default function PinPureActions({
  pin,
  position,
  width = 0,
  height = 0,
  onUpdatePin,
  offsetParent,
  fullWidth,
  showDownload,
  hasPinCreated,
  contextValue,
  showLikeBtn,
  searchWord,
  currentBoard,
  enableCurrentBoardPriority,
}: IPinActionsProps) {
  position = position || {
    x: -9999,
    y: -9999,
  };

  const { user, vips } = useAppStore(({ user, vips }) => ({ user, vips }));

  // 同在画板相关功能
  const { getSingleRelatedBoards } = useRelatedBoards();
  const {
    userBoards,
    ensureUserBoardsLoaded,
    getBoardMatchingForPin,
    updateUserBoards,
    isNewUser,
  } = useBoardMatching();

  // AB 测试：画板推荐创建功能
  const recommendCreateBoardAB = useABTest(AB_RECOMMEND_CREATE_BOARD as any);

  // 获取 lastBoardsReady 状态
  const lastBoardsReady = useAppStore((s) => s.lastBoardsReady);

  // 获取同在画板信息和画板匹配结果
  const [pinWithRelatedBoards, setPinWithRelatedBoards] = useState<any>(pin);
  // 初始状态：如果启用了当前画板优先逻辑（画板详情页），直接设置为空对象，避免显示加载状态
  // 否则设置为 null，这样会显示 "..." 加载状态，等匹配完成后再显示推荐画板或采集至画板
  const [boardMatchingResult, setBoardMatchingResult] = useState<any>(() => {
    if (enableCurrentBoardPriority && currentBoard) {
      return {
        recommendedBoard: null,
        recommendedBoards: [],
        recommendedRelatedBoards: [],
        useUserBoardRecommendation: false,
      };
    }
    return null;
  });

  // 如果启用了当前画板优先逻辑（画板详情页），立即设置 boardMatchingResult，避免显示加载状态
  useEffect(() => {
    if (enableCurrentBoardPriority && currentBoard) {
      setPinWithRelatedBoards(pin);
      setBoardMatchingResult({
        recommendedBoard: null,
        recommendedBoards: [],
        recommendedRelatedBoards: [],
        useUserBoardRecommendation: false,
      });
    }
  }, [enableCurrentBoardPriority, currentBoard, pin]);

  // 初始化时确保用户画板已加载
  useEffect(() => {
    if (!user?.urlname) return;

    const loadUserBoards = async () => {
      try {
        await ensureUserBoardsLoaded();
      } catch (error) {
        console.error("获取用户画板信息失败:", error);
      }
    };

    // 只在还没有加载过用户画板时才请求
    if (!lastBoardsReady) {
      loadUserBoards();
    }
  }, [user?.urlname, ensureUserBoardsLoaded, lastBoardsReady]);

  // 防抖的匹配函数
  const loadRelatedBoards = useCallback(async () => {
    // 如果启用了当前画板优先逻辑（画板详情页），则跳过推荐匹配，直接使用当前画板
    if (enableCurrentBoardPriority && currentBoard) {
      setPinWithRelatedBoards(pin);
      // 设置为空对象而不是 null，避免触发 isMatching 状态
      setBoardMatchingResult({
        recommendedBoard: null,
        recommendedBoards: [],
        recommendedRelatedBoards: [],
        useUserBoardRecommendation: false,
      });
      return;
    }

    // 老版本不获取同在画板数据
    if (!recommendCreateBoardAB.new) {
      setPinWithRelatedBoards(pin);
      // 设置为空对象而不是 null，避免触发 isMatching 状态
      setBoardMatchingResult({
        recommendedBoard: null,
        recommendedBoards: [],
        recommendedRelatedBoards: [],
        useUserBoardRecommendation: false,
      });
      return;
    }

    if (!pin?.pin_id || !user?.urlname) return;

    try {
      // 确保用户画板已加载，如果没有则先获取
      let currentUserBoards = userBoards;
      if (!lastBoardsReady) {
        // console.log("用户画板未加载，先获取用户画板...");
        currentUserBoards = await ensureUserBoardsLoaded();
      }

      // 新版本：当用户创建的画板超过阈值时，不获取同在画板数据，只显示采集至画板
      if (currentUserBoards.length >= USER_BOARD_RECOMMEND_THRESHOLD) {
        setPinWithRelatedBoards(pin);
        // 设置为空对象而不是 null，避免触发 isMatching 状态
        setBoardMatchingResult({
          recommendedBoard: null,
          recommendedBoards: [],
          recommendedRelatedBoards: [],
          useUserBoardRecommendation: false,
        });
        return;
      }

      // 获取同在画板信息
      const relatedBoards = await getSingleRelatedBoards(pin);

      // 进行画板匹配
      const matching = getBoardMatchingForPin(
        pin,
        relatedBoards,
        currentUserBoards
      );

      // 更新状态
      setPinWithRelatedBoards({
        ...pin,
        relatedBoards,
      });
      setBoardMatchingResult(matching);
    } catch (error) {
      console.error("获取同在画板信息失败:", error);
      setPinWithRelatedBoards(pin);
      // 设置为空对象而不是 null，避免触发 isMatching 状态
      setBoardMatchingResult({
        recommendedBoard: null,
        recommendedBoards: [],
        recommendedRelatedBoards: [],
        useUserBoardRecommendation: false,
      });
    }
  }, [
    enableCurrentBoardPriority,
    currentBoard,
    recommendCreateBoardAB.new,
    pin,
    user?.urlname,
    getSingleRelatedBoards,
    getBoardMatchingForPin,
    ensureUserBoardsLoaded,
    lastBoardsReady,
    userBoards,
  ]);

  // 使用防抖函数，300ms 延迟
  const { run: debouncedLoadRelatedBoards } = useDebounceFn(loadRelatedBoards, {
    wait: 300,
  });

  // 当 pin 变化时，触发防抖的匹配
  // 如果启用了当前画板优先逻辑（画板详情页），则跳过匹配，直接使用当前画板
  useEffect(() => {
    if (!pin?.pin_id || !user?.urlname) return;
    // 如果启用了当前画板优先逻辑，不触发匹配，直接使用当前画板
    if (enableCurrentBoardPriority && currentBoard) {
      return;
    }
    debouncedLoadRelatedBoards();
  }, [
    pin?.pin_id,
    user?.urlname,
    enableCurrentBoardPriority,
    currentBoard,
    debouncedLoadRelatedBoards,
  ]);

  const guideRef = useRef<{ hide: () => void }>();
  useEffect(() => {
    const currentGuideRef = guideRef.current;
    return () => {
      // 通过外部控制只展示一次引导
      if (offsetParent && currentGuideRef) {
        currentGuideRef.hide();
      }
    };
  }, [offsetParent]);

  // 当hover的时候给采集卡片加个hover状态
  useEffect(() => {
    offsetParent?.classList?.add("hover");

    return () => {
      offsetParent?.classList?.remove("hover");
    };
  }, [offsetParent]);

  const canPinEdit = useMemo(() => {
    return canEditPinFn(pin, user);
  }, [pin, user]);

  if (!user) {
    return null;
  }
  return createPortal(
    <PinActionProvider contextValue={contextValue}>
      {/* 左上角 */}
      <PositionWrap
        position={position}
        offset={{
          x: 12,
          y: 12,
        }}
        offsetParent={offsetParent}
        customStyle={{
          right: 12,
        }}
      >
        {pin && (
          <PurePinButton
            pin={pinWithRelatedBoards || pin}
            current={!!pin}
            hasPinCreated={hasPinCreated}
            boardMatchingResult={boardMatchingResult}
            onUserBoardUpdate={updateUserBoards}
            isNewUser={isNewUser()}
            searchWord={searchWord}
            currentBoard={currentBoard}
            enableCurrentBoardPriority={enableCurrentBoardPriority}
            needAnimation={true}
            enableBoardRecommend={recommendCreateBoardAB.new}
          />
        )}
      </PositionWrap>

      {/* 右下角 */}
      <PositionWrap
        position={position}
        offset={{
          x: 12 - width,
          y: 12 - height,
        }}
        bottom
        right
        offsetParent={offsetParent}
      >
        {canPinEdit ? (
          <PurePinEdit pin={pin} onUpdatePin={onUpdatePin} size={32} circle />
        ) : null}

        {showLikeBtn && <PinLike pin={pin} onUpdatePin={onUpdatePin} />}
      </PositionWrap>

      {/* 左下角 */}
      {!fullWidth && pin?.trusted !== false && (
        <PositionWrap
          position={position}
          offset={{
            x: 12,
            y: 12 - height,
          }}
          bottom
          offsetParent={offsetParent}
        >
          {showDownload && user && vips.length <= 0 ? (
            <PurePinDownload pin={pin} />
          ) : null}
        </PositionWrap>
      )}
    </PinActionProvider>,
    offsetParent || document.body
  );
}

/**
 * 定位包裹组件
 * */
function PositionWrap({
  children,
  offset,
  position,
  right = false,
  bottom = false,
  offsetParent,
  customStyle,
}: {
  children: React.ReactNode;
  offset?: {
    x: number;
    y: number;
  };
  position: {
    x: number;
    y: number;
  };
  right?: boolean;
  bottom?: boolean;
  offsetParent?: HTMLElement;
  customStyle?: CSSProperties;
}) {
  /** 偏移量 */
  offset = offset || {
    x: 0,
    y: 0,
  };
  const router = useRouter();
  const operate = () => {
    // 关注页面需要记录用户有没有操作
    if (router.pathname === "/follow") {
      window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
    }
  };
  // 计算定位信息
  const style = useMemo<CSSProperties>(() => {
    const obj = {
      opacity: position.x === -9999 ? 0 : 1,
    };

    if (right) {
      Object.assign(obj, {
        right:
          (offsetParent || document.body).clientWidth - position.x + offset.x,
      });
    } else {
      Object.assign(obj, {
        left: position.x + offset.x,
      });
    }

    if (bottom) {
      Object.assign(obj, {
        bottom:
          (offsetParent || document.body).clientHeight - position.y + offset.y,
      });
    } else {
      Object.assign(obj, {
        top: position.y + offset.y,
      });
    }

    if (customStyle) {
      Object.assign(obj, customStyle);
    }

    return obj;
  }, [bottom, customStyle, offset, offsetParent, position, right]);

  return (
    <div className={styles.actionWrap} style={style} onClick={operate}>
      {children}
    </div>
  );
}
